import React from "react";
import tw, { styled } from "twin.macro";
import "styled-components/macro";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import moment from "moment-timezone";
import _ from "lodash";
import { TZ } from "../constants";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import Icon from "../components/Icon";
import { getNextEvent, getNextGame } from "../utils";
import { Link } from "gatsby";
import logoPlaceholder from "../images/no-image_baseball.png";

const LiveButtonContainer = styled(Link)`
  ${tw`
    flex items-center
    py-1 px-4 sm:py-1.5
    text-black  rounded-lg  
    bg-white hover:opacity-80
    w-full sm:w-fit
  `}

  ${({ disabled }) => disabled && tw`pointer-events-none`}
`;
const LiveButtonLink = styled.a`
  ${tw`
    flex items-center
    py-1 px-4 sm:py-1.5
    text-black  rounded-lg  
    bg-white hover:opacity-80
    w-full sm:w-fit
  `}

  ${({ disabled }) => disabled && tw`pointer-events-none`}
`;
const BoxScoreLink = styled.a`
  ${tw`
    flex items-center
    px-3 mx-1
    text-black  rounded-lg  
    bg-white hover:opacity-80
  `}

  ${({ disabled }) => disabled && tw`pointer-events-none`}
  & > .material-icons {
    ${tw`text-3xl`}
    color: ${(props) => props.color}
  }
`;

const LiveText = styled.div`
  ${tw`text-base lg:text-2xl font-bold flex-1 whitespace-nowrap`}
  color: ${(props) => props.color}
`;
const LiveDateTimeContainer = styled.div`
  ${tw`hidden sm:flex flex-col sm:ml-10 text-xs sm:text-sm whitespace-nowrap font-bold`}
`;
const LiveDate = styled.div`
  ${tw`hidden sm:block`}
`;
const LiveTime = styled.div`
  ${tw`hidden sm:block sm:ml-4 text-xs sm:text-sm whitespace-nowrap`}
`;

const LiveMatchContainer = styled.div`
  ${tw`flex ml-3 sm:ml-12 items-center justify-end`}
`;
const AwayTeam = styled.div`
  ${tw`flex items-center`}
`;
const HomeTeam = styled.div`
  ${tw`flex items-center ml-4 sm:ml-8`}
`;
const TeamIcon = styled.div`
  ${(props) =>
    props.large ? tw`w-14 h-8 sm:w-20 sm:h-12` : tw`w-10 h-5 sm:w-16 sm:h-8`}
  ${tw`flex overflow-hidden justify-center items-center`}
`;

const Abbreviation = styled.div`
  ${tw`font-bold`}
  color: ${({ color }) => (color ? color : "#000000")}
`;

const LiveName = styled.div`
  ${tw`font-bold sm:block sm:ml-4 whitespace-nowrap`}
  color: ${({ color }) => (color ? color : "#000000")}
`;
const PlacholderContainer = styled.div`
  ${tw`w-8`}
`;

const NextUpcomingGame = ({ data, site, color, selectedTeam }) => {
  const breakpoints = useBreakpoint();
  const breakpointsSum = Object.values(breakpoints).reduce(
    (a, b) => (b ? a + 1 : a),
    0
  );
  const isTablet = breakpointsSum >= 1;
  const isLargeScreen = breakpointsSum > 3;
  const teamsByName = _.keyBy(
    data.allTeams.edges,
    (edge) =>
      _.get(edge, "node.frontmatter.id", "") ||
      _.get(edge, "node.frontmatter.name", "")
  );
  const nextGame = getNextGame(data.allGames.nodes);
  const nextEvent = getNextEvent(data.allEvents.edges);

  if (
    _.isNil(nextGame) &&
    !_.isNil(nextEvent) &&
    ["dawgs", "academy"].includes(site)
  ) {
    const { startDate, endDate, name } = nextEvent.node.frontmatter;
    const formattedStartDate = moment
      .tz(startDate, TZ)
      .format("MMM DD h:mm A z")
      .toUpperCase();
    const formattedEndDate = moment
      .tz(endDate, TZ)
      .format("MMM DD h:mm A z")
      .toUpperCase();
    return (
      <>
        <LiveButtonContainer href="#" target="_blank" disabled={true}>
          {site === "dawgs" && <LiveText color={color}>WATCH LIVE</LiveText>}
          <LiveTime>
            {formattedStartDate} - {formattedEndDate}
          </LiveTime>
          <LiveName>{name}</LiveName>
        </LiveButtonContainer>
      </>
    );
  } else if (
    _.isNil(nextGame) &&
    _.isNil(nextEvent) &&
    ["dawgs", "academy"].includes(site)
  ) {
    return (
      <>
        <LiveButtonContainer href="#" target="_blank" disabled={true}>
          {site === "dawgs" && <LiveText color={color}>WATCH LIVE</LiveText>}
          <LiveTime>No upcoming games scheduled</LiveTime>
        </LiveButtonContainer>
      </>
    );
  } else if (_.isNil(nextGame)) {
    return <></>;
  }

  const timePassed = moment.tz(TZ).diff(nextGame.frontmatter.date, "minutes");
  const nextGameIsLive = timePassed <= 180 && timePassed >= 0;

  const { homeTeam, awayTeam, date, liveScoreLink, yr, numberSuffix } =
    nextGame.frontmatter;
  const homeTeamDoc = teamsByName[homeTeam];
  const awayTeamDoc = teamsByName[awayTeam];
  const formattedDate = moment.tz(date, TZ).format("MMM DD").toUpperCase();
  const formattedTime = moment.tz(date, TZ).format("h:mm A").toUpperCase();
  let watchLiveLink = _.get(
    data,
    "markdownRemark.frontmatter.hero.watchLiveLink",
    ""
  );
  let teamSeasonLink = _.get(
    data,
    "markdownRemark.frontmatter.hero.teamSeasonLink",
    ""
  );
  if (site === "academy" && selectedTeam) {
    const currentTeam = _.find(
      data.allTeams.edges,
      (edge) => _.toLower(edge.node.frontmatter.name) === selectedTeam
    );
    watchLiveLink = _.get(currentTeam, "node.frontmatter.watchLiveLink", "");
    teamSeasonLink = _.get(currentTeam, "node.frontmatter.teamSeasonLink", "");
  }
  const slug = `/schedule/${_.kebabCase(yr)}/${_.kebabCase(
    `${awayTeam}-${homeTeam}`
  )}-${moment(date).tz(TZ).format("YYYY-MM-DD")}${
    numberSuffix ? `-${numberSuffix}` : ""
  }`;

  const Wrapper = ({ children, site }) => {
    if (["dawgs", "academy"].includes(site)) {
      return (
        <LiveButtonLink
          href={watchLiveLink || teamSeasonLink}
          target="_blank"
          disabled={_.isEmpty(watchLiveLink) && _.isEmpty(teamSeasonLink)}
        >
          {children}
        </LiveButtonLink>
      );
    } else {
      return <LiveButtonContainer to={slug}>{children}</LiveButtonContainer>;
    }
  };
  const awayLogo = getImage(_.get(awayTeamDoc, "node.frontmatter.logo"));
  const homeLogo = getImage(_.get(homeTeamDoc, "node.frontmatter.logo"));
  return (
    <>
      <Wrapper site={site}>
        <LiveText color={color}>
          {["dawgs"].includes(site) ? "WATCH LIVE" : "NEXT GAME"}
        </LiveText>
        {["dawgs", "academy"].includes(site) && (
          <LiveTime>{formattedDate}</LiveTime>
        )}
        {site === "diamondbacks" && (
          <LiveDateTimeContainer>
            <LiveDate>{formattedDate}</LiveDate>
            <LiveTime>{formattedTime}</LiveTime>
          </LiveDateTimeContainer>
        )}
        <LiveMatchContainer>
          <AwayTeam>
            <TeamIcon large={site === "diamondbacks"}>
              {awayLogo ? (
                <GatsbyImage
                  alt="away-team"
                  image={awayLogo}
                  style={{ width: "100%", height: "100%" }}
                  imgStyle={{ width: "100%", height: "100%" }}
                  objectFit="contain"
                />
              ) : (
                <PlacholderContainer>
                  <img
                    src={logoPlaceholder}
                    alt="placeholder"
                    style={{ width: "100%", height: "auto" }}
                    loading="lazy"
                  />
                </PlacholderContainer>
              )}
            </TeamIcon>
            <Abbreviation>
              {isLargeScreen &&
                _.get(awayTeamDoc, "node.frontmatter.location.town", "").split(
                  ","
                )[0]}{" "}
              {isTablet && _.get(awayTeamDoc, "node.frontmatter.name", "")}
            </Abbreviation>
          </AwayTeam>
          <HomeTeam>
            <TeamIcon>
              {homeLogo ? (
                <GatsbyImage
                  alt="home-team"
                  image={homeLogo}
                  style={{ width: "100%", height: "100%" }}
                  imgStyle={{ width: "100%", height: "100%" }}
                  objectFit="contain"
                />
              ) : (
                <PlacholderContainer>
                  <img
                    src={logoPlaceholder}
                    alt="placeholder"
                    style={{ width: "100%", height: "auto" }}
                    loading="lazy"
                  />
                </PlacholderContainer>
              )}
            </TeamIcon>
            <Abbreviation color="#b71c1c">
              {isLargeScreen &&
                _.get(homeTeamDoc, "node.frontmatter.location.town", "").split(
                  ","
                )[0]}{" "}
              {isTablet && _.get(homeTeamDoc, "node.frontmatter.name", "")}
            </Abbreviation>
          </HomeTeam>
        </LiveMatchContainer>
      </Wrapper>
      {nextGameIsLive && (
        <BoxScoreLink
          href={liveScoreLink || teamSeasonLink}
          target="_blank"
          color={color}
        >
          <Icon name="view_module" />
        </BoxScoreLink>
      )}
    </>
  );
};

export default NextUpcomingGame;
